import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookieConsent } from '../context/CookieConsentContext';
import { initAnalytics, trackEvent, trackPageView } from '../utils/analytics';
import { VERTICAL_PATHS } from '../utils/seoInternalLinks';

const VERTICAL_UTM_MAP = {
  [VERTICAL_PATHS.aerial]: 'vertical_view',
  [VERTICAL_PATHS.location]: 'vertical_view',
  [VERTICAL_PATHS.ecommerce]: 'vertical_view',
};

/**
 * Route-level GA4 events (consent-gated). Set REACT_APP_GA4_MEASUREMENT_ID to enable.
 */
export default function SeoAnalytics() {
  const { pathname, search } = useLocation();
  const { consent } = useCookieConsent();
  const blogScrollFired = useRef(false);

  useEffect(() => {
    initAnalytics(consent.analytics);
  }, [consent.analytics]);

  useEffect(() => {
    if (!consent.analytics) return;
    trackPageView(`${pathname}${search}`);

    if (pathname === '/pricing') {
      trackEvent('pricing_view', { page_path: pathname });
    }

    if (VERTICAL_UTM_MAP[pathname]) {
      trackEvent('vertical_view', { page_path: pathname, vertical: pathname });
    }

    if (pathname === '/login' && search.includes('mode=signup')) {
      trackEvent('signup_started', { page_path: pathname });
    }

    blogScrollFired.current = false;
  }, [pathname, search, consent.analytics]);

  useEffect(() => {
    if (!consent.analytics || !pathname.startsWith('/blog/') || pathname === '/blog') {
      return undefined;
    }

    const onScroll = () => {
      if (blogScrollFired.current) return;
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      if (max <= 0) return;
      const ratio = doc.scrollTop / max;
      if (ratio >= 0.75) {
        blogScrollFired.current = true;
        trackEvent('blog_read_complete', { page_path: pathname });
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname, consent.analytics]);

  return null;
}
